import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  SettingsLocation,
  ApplicationUser,
  WorkOrderStatus,
  ServiceCodes,
  WorkOrderPriorityCode,
} from "../../models/lookupTypes";
import {
  WorkOrder,
  Filter,
  FilterDefState,
  WorkOrderByStDefList,
  WorkOrdersByTechDefList,
  woStatusDefState,
  locationsDefState,
  WorkOrdersByStatus,
  WorkOrdersByTechnician,
  techDefState,
  salesPersonDefState,
  woPriorityCodeDefState,
  MeterTypeList,
  MeterType,
  serviceCodeDefState,
  WorkOrderCollection,
  Customer,
  //Some test to be checked
  Segment,
  WorkOrderSegmentDetail,
  defWorkOrderState,
} from "../QueueBoard/models/workOrderTypes";
import { WorkOrderQueueAPI } from "../QueueBoard/api/workOrderApi";
import { Unit } from "../QueueBoard/models/workOrderTypes";

interface WorkOrderState {
  WorkOrdersByStatus: WorkOrdersByStatus[];
  WorkOrdersByTechnician: WorkOrdersByTechnician[];
  filters: Filter;
  statuses: string[];
  currentWorkOrder: WorkOrder | null;
  currentWorkOrderHistory: WorkOrder | null;
  currentSegment: Segment | null;
  locations: SettingsLocation[];
  techniciansForFilters: ApplicationUser[];
  technicians: ApplicationUser[];
  salesPersons: ApplicationUser[];
  woStatuses: WorkOrderStatus[];
  woPriorityList: WorkOrderPriorityCode[];
  meterTypes: MeterType[];
  serviceCodes: ServiceCodes[];
  loading: boolean;
  error: string;
  currentTabIndex: number;
  isSegmentUpdateOpen: boolean;
  segmentIndex: number;
  currentSegmentDetails: WorkOrderSegmentDetail | null;
}
const initialState: WorkOrderState = {
  WorkOrdersByStatus: WorkOrderByStDefList,
  WorkOrdersByTechnician: WorkOrdersByTechDefList,
  filters: FilterDefState(),
  statuses: [],
  currentWorkOrder: defWorkOrderState, // Actual value that matches the type `WorkOrder | null`
  currentWorkOrderHistory: defWorkOrderState,
  currentSegment: null,
  locations: locationsDefState,
  technicians: techDefState,
  techniciansForFilters: techDefState,
  woStatuses: woStatusDefState,
  salesPersons: salesPersonDefState,
  woPriorityList: woPriorityCodeDefState,
  serviceCodes: serviceCodeDefState,
  loading: false,
  error: "",
  currentTabIndex: 0,
  isSegmentUpdateOpen: false,
  meterTypes: MeterTypeList,
  segmentIndex: null,
  currentSegmentDetails: null,
};

//For changing the status of work order from one status column to another
interface UpdateWorkOrderStatusPayload {
  SourceStatusId: number;
  SourceItems: WorkOrder[];
  DestStatusId: number;
  DestItems: WorkOrder[];
}

//For changing the position in the same column
interface UpdateWorkOrderPositionsPayload {
  statusId?: number;
  copiedItems: WorkOrder[];
}
interface WorkOrderStatusUpdate {
  sourceStatusIdentity: number;
  destStatusIdentity: number;
  invoiceDocumentIdentity: number;
}
// Async thunks for fetching data
export const fetchWorkOrders = createAsyncThunk<WorkOrderCollection, any>(
  "workOrders/fetchWorkOrders",
  //"workOrders/GetWorkOrdersByTechnician",
  async (payload: any) => {
    try {
      const response = await WorkOrderQueueAPI.getWorkOrdersBystatus(payload);
      if (response === null) {
        throw new Error("No data available"); // Handle null response or return a default value
      }
      return response; // Ensure this is the correct type
    } catch (error) {
      // Handle error or throw to let the thunk handle it
      throw error;
    }
  },
);

const workOrderSlice = createSlice({
  name: "workOrderBoard",
  initialState,
  reducers: {
    //#region Lookups
    setLocationLookup: (state, action) => {
      state.locations = action.payload.locations;
    },
    setTechnicianLookupForFilters: (state, action) => {
      state.techniciansForFilters = action.payload.technicians;
    },
    setTechnicianLookup: (state, action) => {
      state.technicians = action.payload.techniciansForWODetail;
    },
    setWoStatusLookup: (state, action) => {
      state.woStatuses = action.payload.WorkOrderStatuses;
    },
    setWoPriorityListLookup: (state, action) => {
      state.woPriorityList = action.payload.woPriorityList;
    },
    setSalesPersonsLookup: (state, action) => {
      state.salesPersons = action.payload.salesPersons;
    },
    setServiceCodesLookup: (state, action) => {
      state.serviceCodes = action.payload.serviceCodes;
    },
    //#endregion

    //#region Queue Board
    setFilters: (state, action) => {
      //
      // console.log("filter key: " + action.payload.key);
      // console.log("filter value: " + action.payload.value);

      state.filters[action.payload.key] = action.payload.value;
    },
    resetFilters: (state) => {
      //
      state.filters.view = 0;
      state.filters.statusIdentity = undefined;
      state.filters.technicianIdentity = undefined;
      state.filters.customerIdentity = undefined;
      state.filters.locationIdentity = undefined;
      state.filters.workOrderFromDate = undefined;
      state.filters.workOrderToDate = undefined;
      state.filters.genericSearchString = undefined;
      state.filters.WorkOrderStatuses = [];
      state.filters.workOrderTechs = [];
      state.filters.locations = "";
      state.filters.searchString = "";
      state.filters.workOrderCreationStartDate = undefined;
      state.filters.workOrderCreationEndDate = undefined;
      state.filters.workOrderScheduleFromDate = undefined;
      state.filters.workOrderScheduleToDate = undefined;

      state.filters.workOrderDeliveryFromDate = undefined;
      state.filters.workOrderDeliveryToDate = undefined;
      state.filters.workOrderPickupFromDate = undefined;
      state.filters.workOrderPickupToDate = undefined;

      state.filters.filterClockedHours = undefined;
      state.filters.filterOverClockedHours = undefined;
      state.filters.woCustomers = [];
    },
    setSpecificWorkOrderStatus: (
      state,
      action: PayloadAction<WorkOrderStatusUpdate>,
    ) => {
      const {
        sourceStatusIdentity,
        destStatusIdentity,
        invoiceDocumentIdentity,
      } = action.payload;

      const column = state.WorkOrdersByStatus.find(
        (status) => status.identity === sourceStatusIdentity,
      );

      const workOrder = column?.workOrders.find(
        (wo) => wo.invoiceDocumentIdentity === invoiceDocumentIdentity,
      )!;

      workOrder.WorkOrderStatusIdentity = destStatusIdentity;
    },
    updateWorkOrderStatusColumns: (
      state,
      action: PayloadAction<UpdateWorkOrderStatusPayload>,
    ) => {
      const { SourceStatusId, SourceItems, DestStatusId, DestItems } =
        action.payload;
      // Find the source and destination status columns
      const sourceColumn = state.WorkOrdersByStatus.find(
        (status) => status.identity === SourceStatusId,
      );
      const destColumn = state.WorkOrdersByStatus.find(
        (status) => status.identity === DestStatusId,
      );

      // Update the work orders in the source column
      if (sourceColumn) {
        sourceColumn.workOrders = SourceItems;
      }
      // Update the work orders in the destination column
      if (destColumn) {
        destColumn.workOrders = DestItems;
      }
    },
    updateWorkOrderPositions: (
      state,
      action: PayloadAction<UpdateWorkOrderPositionsPayload>,
    ) => {
      const { statusId, copiedItems } = action.payload;
      const column = state.WorkOrdersByStatus.find(
        (status) => status.identity === statusId,
      );
      if (column) {
        const updatedCopiedItemsWithIndices = copiedItems.map(
          (workOrder, index) => ({
            ...workOrder,
            position: index,
          }),
        );

        column.workOrders = updatedCopiedItemsWithIndices;
      }
    },
    resetQueueBoards: (state, action) => {
      state.WorkOrdersByStatus = [];
      state.WorkOrdersByTechnician = [];
    },
    //#endregion

    //#region Add/Update Work Order

    setCurrentWorkOrder(state, action: PayloadAction<WorkOrder>) {
      state.currentWorkOrder = action.payload;
    },
    setCurrentWorkOrderHistory(state, action: PayloadAction<WorkOrder>) {
      state.currentWorkOrderHistory = action.payload;
    },
    setCurrentWorkOrderStatus: (state, action) => {
      //state.currentWorkOrder.WorkOrderStatusIdentity = action.payload;
      if (state.currentWorkOrder) {
        state.currentWorkOrder.WorkOrderStatusIdentity = action.payload;
      } else {
        // Handle the case where currentWorkOrder is null, if necessary
        // For example, you could log a warning or set a default value
        console.warn("Work Order is null");
      }
    },
    clearCurrentWorkOrder: (state, action) => {
      state.currentWorkOrder = null;
    },
    addWorkOrder(state, action: PayloadAction<WorkOrder>) {
      //state.workOrders.push(action.payload);
    },
    updateCurrentWorkOrder(state, action: PayloadAction<WorkOrder>) {
      // const index = state.workOrders.findIndex(
      //   (wo) => wo.identity === action.payload.identity
      // );
      // if (index !== -1) {
      //   state.workOrders[index] = action.payload;
      // }
    },
    setWorkOrderCurrentTab: (state, action) => {
      state.currentTabIndex = action.payload;
    },
    setWorkOrderUnit: (state, action: PayloadAction<Unit>) => {
      if (state.currentWorkOrder) {
        state.currentWorkOrder.stockNumber = action.payload.unitNumber;
        state.currentWorkOrder.model = action.payload.model;
        state.currentWorkOrder.meterType = action.payload.meterType;
        state.currentWorkOrder.meterValue = action.payload.meterValue;
      }
    },
    setCurrentWorkOrderDescription: (state, action) => {
      state.currentWorkOrder!.description = action.payload;
    },
    setCurrentWorkOrderLocation: (state, action) => {
      if (state.currentWorkOrder) {
        state.currentWorkOrder!.locationIdentity = action.payload;
      }
    },
    setCurrentWorkOrderTech: (state, action) => {
      if (state.currentWorkOrder) {
        state.currentWorkOrder!.technicianIdentity = action.payload;
      }
    },
    setCurrentWorkOrderPriority: (state, action) => {
      if (state.currentWorkOrder) {
        state.currentWorkOrder!.woPriorityIdentity = action.payload;
      }
    },
    setCurrentWorkOrderSales: (
      state,
      action: PayloadAction<ApplicationUser>,
    ) => {
      if (state.currentWorkOrder) {
        state.currentWorkOrder!.salesPersonIdentity = action.payload.identity;
        state.currentWorkOrder!.salesPersonName = action.payload.userName;
      }
    },
    setWorkOrderCustomer: (state, action: PayloadAction<Customer>) => {
      if (state.currentWorkOrder) {
        state.currentWorkOrder!.customerName = action.payload.name;
        state.currentWorkOrder!.customerIdentity = action.payload.identity;
      }
    },
    setCurrentSegment: (state, action: PayloadAction<Segment>) => {
      state.currentSegment = action.payload;
    },
    clearCurrentSegment: (state) => {
      state.currentSegment = null;
    },
    setSegmentIndex: (state, action) => {
      state.segmentIndex = action.payload;
    },
    setSegmentUpdateOpen: (state) => {
      state.isSegmentUpdateOpen = true;
    },
    setSegmentUpdateClose: (state) => {
      state.isSegmentUpdateOpen = false;
    },
    setCurrentSegmentDetails: (
      state,
      action: PayloadAction<WorkOrderSegmentDetail>,
    ) => {
      state.currentSegmentDetails = action.payload;
    },
    //#endregion
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkOrders.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchWorkOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.WorkOrdersByStatus = action.payload.orderByStatus || [];
        state.WorkOrdersByTechnician = action.payload.orderByTechnician || [];
        // const woBystatuses = action.payload.orderBystatus;
        // const woBtTechs = action.payload.orderByTechnician;
        // const sortedstatuses = woBystatuses.map((status) => {
        //   const sortedWorkOrders = status.workOrders
        //     .slice()
        //     .sort((a, b) => a.statusPosition - b.statusPosition);
        //   return { ...status, workOrders: sortedWorkOrders };
        // });

        // const sortedTech = woBtTechs.map((status) => {
        //   const sortedWorkOrders = status.workOrders
        //     .slice()
        //     .sort((a, b) => a.techPosition - b.techPosition);
        //   return { ...status, workOrders: sortedWorkOrders };
        // });
        // state.WorkOrdersBystatus = sortedstatuses;
        // state.WorkOrdersByTechnician = sortedTech;
      })
      .addCase(fetchWorkOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  resetFilters,
  setCurrentWorkOrderStatus,
  setTechnicianLookupForFilters,
  setCurrentWorkOrderDescription,
  clearCurrentWorkOrder,
  updateCurrentWorkOrder,
  setCurrentWorkOrderTech,
  addWorkOrder,
  setSpecificWorkOrderStatus,
  updateWorkOrderStatusColumns,
  updateWorkOrderPositions,
  setCurrentWorkOrder,
  setLocationLookup,
  setWoPriorityListLookup,
  setSalesPersonsLookup,
  setServiceCodesLookup,
  setWoStatusLookup,
  setWorkOrderCurrentTab,
  setTechnicianLookup,
  resetQueueBoards,
  setWorkOrderUnit,
  setWorkOrderCustomer,
  setCurrentWorkOrderLocation,
  setCurrentWorkOrderSales,
  setCurrentWorkOrderPriority,
  setCurrentSegment,
  clearCurrentSegment,
  setSegmentUpdateOpen,
  setSegmentUpdateClose,
  setSegmentIndex,
  setCurrentSegmentDetails,
} = workOrderSlice.actions;
export default workOrderSlice.reducer;
